import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

/**
 * Theme Context - Manages light/dark mode across the application
 * Uses CSS variables defined in theme.css for consistent styling
 */

const ThemeContext = createContext();

// Local storage key for persisting theme preference
const THEME_STORAGE_KEY = "clinica-theme";

// Theme values
export const THEMES = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
};

/**
 * ThemeProvider component that wraps the app and provides theme state
 */
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or default to light mode
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return THEMES.LIGHT;
    
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme && Object.values(THEMES).includes(savedTheme)) {
      return savedTheme;
    }
    return THEMES.LIGHT;
  });

  // Track the actual applied theme (resolved from system if needed)
  const [resolvedTheme, setResolvedTheme] = useState(THEMES.LIGHT);

  // Check system preference
  const getSystemTheme = useCallback(() => {
    if (typeof window === "undefined") return THEMES.LIGHT;
    return window.matchMedia("(prefers-color-scheme: dark)").matches 
      ? THEMES.DARK 
      : THEMES.LIGHT;
  }, []);

  // Apply theme to document
  const applyTheme = useCallback((themeToApply) => {
    const root = document.documentElement;
    
    if (themeToApply === THEMES.DARK) {
      root.setAttribute("data-theme", "dark");
      root.classList.add("dark");
    } else {
      root.removeAttribute("data-theme");
      root.classList.remove("dark");
    }
    
    setResolvedTheme(themeToApply);
  }, []);

  // Effect to apply theme when it changes
  useEffect(() => {
    const actualTheme = theme === THEMES.SYSTEM ? getSystemTheme() : theme;
    applyTheme(actualTheme);
    
    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme, applyTheme, getSystemTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== THEMES.SYSTEM) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    
    const handleChange = (e) => {
      applyTheme(e.matches ? THEMES.DARK : THEMES.LIGHT);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }
    
    // Fallback for older browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [theme, applyTheme]);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      if (current === THEMES.DARK) return THEMES.LIGHT;
      if (current === THEMES.LIGHT) return THEMES.DARK;
      // If system, toggle to the opposite of current system theme
      return getSystemTheme() === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK;
    });
  }, [getSystemTheme]);

  // Set a specific theme
  const setThemeValue = useCallback((newTheme) => {
    if (Object.values(THEMES).includes(newTheme)) {
      setTheme(newTheme);
    }
  }, []);

  const value = {
    theme,
    resolvedTheme,
    isDark: resolvedTheme === THEMES.DARK,
    toggleTheme,
    setTheme: setThemeValue,
    THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * Hook to access theme context
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export { ThemeContext };
export default ThemeProvider;
